import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getData } from "../../services/FetchNodeservices";
import { FaArrowLeft, FaFileWord, FaPrint } from "react-icons/fa";
import { saveAs } from "file-saver";

export default function BajajSummarySheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editable candidate fields state
  const [fields, setFields] = useState({
    name: "",
    contactNo: "",
    email: "",
    maritalStatus: "",
    dob: "",
    gender: "",
    roleAppliedFor: "",
    reportingTo: "",
    reportees: "",
    revenueAccountability: "",
    
    // PG
    pgDegree: "",
    pgInstitute: "",
    pgYear: "",
    pgScore: "",
    pgTime: "",

    // UG
    ugDegree: "",
    ugInstitute: "",
    ugYear: "",
    ugScore: "",
    ugTime: "",

    tenthPercent: "",
    twelfthPercent: "",

    totalExp: "",
    currentOrg: "",
    tenureCurrentOrg: "",
    currentDesignation: "",

    currentLocation: "",
    locationAppliedFor: "",
    nativeLocation: "",
    otherLocations: "",

    currentCtc: "",
    expectedCtc: "",
    noticePeriod: "",
    reasonForChange: "",

    appraisalDate: "",
    lastAppraisalPercent: "",
    lastBonus: "",
    promotionDue: "",

    revAccountabilityShared: "",
    targetOrg: "",
    jdShared: "",

    // Recruiter comments section
    jdMatch: "",
    communicationSkills: "",
    technicalSkills: "",
    reasonForLoading: "",
  });

  const parseQualifications = (qualStr) => {
    if (!qualStr || typeof qualStr !== "string") {
      return { ugDegree: "", pgDegree: "" };
    }

    const str = qualStr.trim();
    if (!str) return { ugDegree: "", pgDegree: "" };

    const isPgDegree = (item) => {
      const s = item.trim().toLowerCase();
      return (
        /\b(mba|m\.?tech|m\.?e|m\.?sc|m\.?com|m\.?a|m\.?s|m\.?pharma|m\.?ed|m\.?des|mca|ll\.?m|pgdm|pgdca|pgdbm|pgd|pg)\b/i.test(s) ||
        s.includes("master") ||
        s.includes("post grad") ||
        s.includes("post-grad")
      );
    };

    const isUgDegree = (item) => {
      const s = item.trim().toLowerCase();
      return (
        /\b(b\.?tech|b\.?e|b\.?sc|b\.?com|b\.?a|b\.?s|b\.?pharma|b\.?ed|b\.?des|bca|bba|ll\.?b|b\.?arch|ug)\b/i.test(s) ||
        s.includes("bachelor") ||
        s.includes("graduat")
      );
    };

    const tokens = str.split(/[,/|&+]|\band\b/i).map((t) => t.trim()).filter(Boolean);

    let pgDegree = "";
    let ugDegree = "";

    if (tokens.length > 1) {
      const pgs = [];
      const ugs = [];
      tokens.forEach((t) => {
        if (isPgDegree(t)) {
          pgs.push(t);
        } else if (isUgDegree(t)) {
          ugs.push(t);
        } else {
          ugs.length === 0 ? ugs.push(t) : pgs.push(t);
        }
      });
      pgDegree = pgs.join(", ");
      ugDegree = ugs.join(", ");
    } else {
      if (isPgDegree(str)) {
        pgDegree = str;
        ugDegree = "";
      } else {
        ugDegree = str;
        pgDegree = "";
      }
    }

    return { ugDegree, pgDegree };
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const res = await getData(`candidate/details/${id}`);
        if (res.status && res.data) {
          const d = res.data;
          const { ugDegree, pgDegree } = parseQualifications(d.qualification || "");
          setFields((prev) => ({
            ...prev,
            name: `${d.f_name || ""} ${d.l_name || ""}`.trim() || d.candidate_name || "",
            contactNo: d.phone_number || "",
            email: d.email_id || "",
            roleAppliedFor: d.job_title || d.designation || "",
            totalExp: d.experience || "",
            currentOrg: d.current_company || "",
            currentDesignation: d.designation || "",
            currentLocation: d.hometown || "",
            currentCtc: d.current_ctc || "",
            expectedCtc: d.expected_ctc || "",
            noticePeriod: d.notice_period || "",
            ugDegree: ugDegree,
            pgDegree: pgDegree,
            reasonForChange: d.reason_for_change || "",
          }));
        } else {
          setError("Candidate details not found.");
        }
      } catch (err) {
        console.error("fetchDetails error:", err);
        setError("Failed to fetch candidate details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const handleChange = (key, value) => {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToWord = () => {
    const htmlString = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8">
  <title>Bajaj Summary Sheet</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page Section1 {
      size: 595.3pt 841.9pt; /* A4 size in points */
      margin: 36.0pt 36.0pt 36.0pt 36.0pt; /* 0.5 in margins on all sides */
      mso-header-margin: 35.4pt;
      mso-footer-margin: 35.4pt;
      mso-paper-source: 0;
    }
    div.Section1 {
      page: Section1;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 9.5pt;
      color: #000000;
      margin: 0;
      padding: 0;
    }
    table.bajaj-table {
      border-collapse: collapse;
      width: 100%;
      border: 1.5px solid #000000;
      table-layout: fixed;
    }
    table.bajaj-table td {
      border: 1px solid #000000;
      padding: 4px 6px 4px 8px; /* Added left padding */
      font-size: 9.5pt;
      vertical-align: middle;
      font-family: Arial, sans-serif;
      color: #000000;
      word-wrap: break-word;
      text-align: left !important; /* Force left alignment */
    }
    .text-center { text-align: left !important; }
    .text-left { text-align: left !important; }
    .font-bold { font-weight: bold; }
    
    .bg-header { background-color: #d9d9d9; }
    .bg-yellow { background-color: #fff2cc; }
    .bg-green { background-color: #e2f0d9; }
    .bg-blue { background-color: #d9e1f2; }
    .bg-gray { background-color: #f2f2f2; }
    .bg-orange { background-color: #fce4d6; }
  </style>
</head>
<body>
  <div class="Section1">
    <table class="bajaj-table">
    <tbody>
      <!-- Header row 1 -->
      <tr>
        <td colspan="8" class="text-left font-bold bg-header" bgcolor="#d9d9d9" style="font-size: 12px; padding: 8px 12px; text-align: left;">
          MORGAN PLACEMENT & CONSULTANCY
        </td>
      </tr>
      <!-- Header row 2 -->
      <tr>
        <td colspan="8" class="text-center font-bold" style="font-size: 11px; padding: 6px;">
          Summary Sheet
        </td>
      </tr>
      
      <!-- Row 1: Name / Contact -->
      <tr>
        <td colspan="1" class="font-bold">Name</td>
        <td colspan="3">${fields.name}</td>
        <td colspan="2" class="font-bold">Contact No.</td>
        <td colspan="2">${fields.contactNo}</td>
      </tr>
      
      <!-- Row 2: Email / Marital / DOB / Gender -->
      <tr>
        <td colspan="1" class="font-bold">Email ID</td>
        <td colspan="2">${fields.email}</td>
        <td colspan="1" class="font-bold">Marital Status:</td>
        <td colspan="1">${fields.maritalStatus}</td>
        <td colspan="1" class="font-bold">DOB-</td>
        <td colspan="1">${fields.dob}</td>
        <td colspan="1" class="font-bold">Gender: ${fields.gender}</td>
      </tr>
      
      <!-- Row 3: Role / Reporting -->
      <tr>
        <td colspan="2" class="font-bold bg-yellow" bgcolor="#fff2cc">Role Applied for</td>
        <td colspan="2" class="text-center bg-yellow" bgcolor="#fff2cc">${fields.roleAppliedFor}</td>
        <td colspan="2" class="font-bold bg-yellow" bgcolor="#fff2cc">Reporting to (Designation)</td>
        <td colspan="2" class="text-center bg-yellow" bgcolor="#fff2cc">${fields.reportingTo}</td>
      </tr>
      
      <!-- Row 4: Reportees / Revenue -->
      <tr>
        <td colspan="2" class="font-bold bg-yellow" bgcolor="#fff2cc">Reportees (Designation)</td>
        <td colspan="2" class="text-center bg-yellow" bgcolor="#fff2cc">${fields.reportees}</td>
        <td colspan="2" class="font-bold bg-yellow" bgcolor="#fff2cc">Revenue accountability<br />( if applicable)</td>
        <td colspan="2" class="text-center bg-yellow" bgcolor="#fff2cc">${fields.revenueAccountability}</td>
      </tr>
      
      <!-- PG Header -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="2" rowspan="3" class="font-bold text-center" style="vertical-align: middle;">PG- ${fields.pgDegree}</td>
        <td colspan="3" class="font-bold text-center">Name of Institute</td>
        <td colspan="2" class="font-bold text-center">Year (From -To)</td>
        <td colspan="1" class="font-bold text-center">Score</td>
      </tr>
      <!-- PG Details -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="3" class="text-center">${fields.pgInstitute}</td>
        <td colspan="2" class="text-center">${fields.pgYear}</td>
        <td colspan="1" class="text-center">${fields.pgScore}</td>
      </tr>
      <!-- PG Time -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="3"></td>
        <td colspan="2" class="font-bold text-left">Full Time /Part Time ( F/P) :</td>
        <td colspan="1" class="text-center">${fields.pgTime}</td>
      </tr>
      
      <!-- UG Header -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="2" rowspan="3" class="font-bold text-center" style="vertical-align: middle;">Graduation- ${fields.ugDegree}</td>
        <td colspan="3" class="font-bold text-center">Name of Institute</td>
        <td colspan="2" class="font-bold text-center">Year (From -To)</td>
        <td colspan="1" class="font-bold text-center">Score</td>
      </tr>
      <!-- UG Details -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="3" class="text-center">${fields.ugInstitute}</td>
        <td colspan="2" class="text-center">${fields.ugYear}</td>
        <td colspan="1" class="text-center">${fields.ugScore}</td>
      </tr>
      <!-- UG Time -->
      <tr class="bg-green" bgcolor="#e2f0d9">
        <td colspan="3"></td>
        <td colspan="2" class="font-bold text-left">Full Time /Part Time ( F/P) :</td>
        <td colspan="1" class="text-center">${fields.ugTime}</td>
      </tr>
      
      <!-- 10th & 12th -->
      <tr>
        <td colspan="2" class="font-bold text-center bg-gray" bgcolor="#f2f2f2">10th %</td>
        <td colspan="2" class="text-center">${fields.tenthPercent}</td>
        <td colspan="2" class="font-bold text-center bg-gray" bgcolor="#f2f2f2">12th %</td>
        <td colspan="2" class="text-center">${fields.twelfthPercent}</td>
      </tr>
      
      <!-- Exp / Org Headers -->
      <tr class="bg-blue" bgcolor="#d9e1f2">
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Total<br />Experience (in<br />Years)</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Current Organization</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Tenure in Current<br />Organization ( in Years)</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Current Designation</td>
      </tr>
      <!-- Exp / Org Values -->
      <tr>
        <td colspan="2" class="text-center">${fields.totalExp}</td>
        <td colspan="2" class="text-center">${fields.currentOrg}</td>
        <td colspan="2" class="text-center">${fields.tenureCurrentOrg}</td>
        <td colspan="2" class="text-center">${fields.currentDesignation}</td>
      </tr>
      
      <!-- Location Headers -->
      <tr class="bg-gray" bgcolor="#f2f2f2">
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Current Location</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Location Applied for</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Native location</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Capture other locations s/he might be<br />interested in</td>
      </tr>
      <!-- Location Values -->
      <tr>
        <td colspan="2" class="text-center">${fields.currentLocation}</td>
        <td colspan="2" class="text-center">${fields.locationAppliedFor}</td>
        <td colspan="2" class="text-center">${fields.nativeLocation}</td>
        <td colspan="2" class="text-center">${fields.otherLocations}</td>
      </tr>
      
      <!-- CTC Headers -->
      <tr class="bg-orange" bgcolor="#fce4d6">
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Current CTC</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Expected CTC ( Mandatory) or %<br />increase</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Notice Period</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Reason for Job Change</td>
      </tr>
      <!-- CTC Values -->
      <tr>
        <td colspan="2" class="text-center">${fields.currentCtc}</td>
        <td colspan="2" class="text-center">${fields.expectedCtc}</td>
        <td colspan="2" class="text-center">${fields.noticePeriod}</td>
        <td colspan="2" class="text-center">${fields.reasonForChange}</td>
      </tr>
      
      <!-- Appraisal Headers -->
      <tr class="bg-orange" bgcolor="#fce4d6">
        <td colspan="2" class="font-bold text-center" style="font-size: 9px;">Appraisal<br />received Date</td>
        <td colspan="2" class="font-bold text-center" style="font-size: 10px;">Last Appraisal %</td>
        <td colspan="2" rowspan="2" class="font-bold text-center" style="font-size: 10px; vertical-align: middle;">
          Last Bonus received<br/>
          <span style="font-weight: normal;">${fields.lastBonus}</span>
        </td>
        <td colspan="2" rowspan="2" class="font-bold text-center" style="font-size: 10px; vertical-align: middle;">
          Promotion Due (if any) ( Y/N)<br/>
          <span style="font-weight: normal;">${fields.promotionDue}</span>
        </td>
      </tr>
      <!-- Appraisal Values -->
      <tr>
        <td colspan="2" class="text-center">${fields.appraisalDate || '(mention due if<br />applicable)'}</td>
        <td colspan="2" class="text-center">${fields.lastAppraisalPercent || '(%)'}</td>
      </tr>
      
      <!-- JD Accountability Headers -->
      <tr class="bg-yellow" bgcolor="#fff2cc">
        <td colspan="3" class="font-bold text-left" style="font-size: 9px; line-height: 1.2;">
          Whether revenue accountability of proposed role has been shared or not :- <br/>(Y/N)
        </td>
        <td colspan="3" class="font-bold text-left" style="font-size: 9px; line-height: 1.2;">
          Whether is from Target list of Organizations? : <br/>( Y/N)
        </td>
        <td colspan="2" class="font-bold text-left" style="font-size: 9px; line-height: 1.2;">
          Whether the JD has been shared or not :- <br/>(Y/N)
        </td>
      </tr>
      <!-- JD Accountability Values -->
      <tr>
        <td colspan="3" class="text-center">${fields.revAccountabilityShared}</td>
        <td colspan="3" class="text-center">${fields.targetOrg}</td>
        <td colspan="2" class="text-center">${fields.jdShared}</td>
      </tr>
    </tbody>
      <!-- Recruiter Comment Header -->
      <tr>
        <td colspan="8" class="text-left font-bold underline" style="font-size: 11px; padding: 8px 10px; text-align: left;">
          Recruiter Comment:
        </td>
      </tr>
      <tr>
        <td colspan="8" class="text-left font-bold" style="font-size: 11px; padding: 6px 10px; text-align: left;">
          Please mention your assessment of Profile matching with the Job Description in terms of :-
        </td>
      </tr>
      <tr>
        <td colspan="8" class="text-left font-bold" style="font-size: 11px; padding: 6px 10px; text-align: left;">
          Match of Profile with the JD ( Yes/No):- <span style="font-weight: normal; text-decoration: underline;">${fields.jdMatch}</span>
        </td>
      </tr>
      <tr>
        <td colspan="8" class="text-left font-bold" style="font-size: 11px; padding: 6px 10px; text-align: left;">
          Communication Skills ( Spoken English and Articulation ) ( High /Low):- <span style="font-weight: normal; text-decoration: underline;">${fields.communicationSkills}</span>
        </td>
      </tr>
      <tr>
        <td colspan="8" class="text-left font-bold" style="font-size: 11px; padding: 8px 10px; text-align: left;">
          Technical Skills ( Whether technical requirements mapped in JD are found in the profile) ( Mention what technical skills found):-
          <div style="margin-top: 6px; font-weight: normal; font-style: italic; border: 1px solid #d9d9d9; padding: 6px 10px; min-height: 40px; background-color: #ffffff; text-align: left;">
            ${fields.technicalSkills || ""}
          </div>
        </td>
      </tr>
      <tr>
        <td colspan="8" class="text-left font-bold" style="font-size: 11px; padding: 6px 10px; text-align: left;">
          Reason for loading the CV against the job requisition :- <span style="font-weight: normal; text-decoration: underline;">${fields.reasonForLoading}</span>
        </td>
      </tr>
    </tbody>
  </table>
  </div>
</body>
</html>
    `;

    const blob = new Blob([htmlString], { type: "application/msword;charset=utf-8" });
    const formattedName = fields.name ? fields.name.trim().replace(/\s+/g, "_") : "Candidate";
    saveAs(blob, `Bajaj_summary_sheet_${formattedName}.doc`);
  };

  const handleStatusChange = () => {
    // Left empty since we are not changing candidate status database values in this separate preview page
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-xs text-gray-500 font-bold uppercase tracking-wider bg-slate-50">
        <svg className="w-10 h-10 text-emerald-600 animate-spin mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Loading Bajaj Summary Sheet...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-xs text-red-500 font-bold uppercase tracking-wider bg-slate-50 gap-3">
        <span>{error}</span>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100/30 p-4 sm:p-8 font-sans antialiased text-gray-900 print:bg-white print:p-0">
      
      {/* Top action controls bar */}
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between no-print bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
        <button
          onClick={() => window.close()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl text-xs font-bold transition duration-150 cursor-pointer"
        >
          <FaArrowLeft className="w-3.5 h-3.5" /> Close Tab
        </button>
        
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 bg-slate-100 px-3 py-1.5 rounded-lg border border-gray-200/40">
            Click cells to edit values inline
          </span>
          <button
            onClick={exportToWord}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition duration-150 shadow-md cursor-pointer mr-1"
          >
            <FaFileWord className="w-4 h-4" /> Export to Word
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition duration-150 shadow-md cursor-pointer"
          >
            <FaPrint className="w-4 h-4" /> Download / Print PDF
          </button>
        </div>
      </div>

      {/* Main Print Container Sheet */}
      <div className="max-w-4xl mx-auto bg-white p-8 border border-gray-200 shadow-2xl rounded-3xl print:border-none print:shadow-none print:p-0 print:m-0 print:rounded-none">
        
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            @page {
              size: A4;
              margin: 10mm 15mm;
            }
            body {
              background: white !important;
              color: black !important;
              font-size: 9.5pt !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .no-print {
              display: none !important;
            }
            input, textarea {
              border: none !important;
              outline: none !important;
              background: transparent !important;
              padding: 0 !important;
              margin: 0 !important;
              box-shadow: none !important;
              font-size: inherit !important;
              font-family: inherit !important;
              font-weight: inherit !important;
              color: black !important;
              resize: none !important;
            }
            td {
              border: 1px solid #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              padding: 3px 5px 3px 8px !important; /* Compact printable padding with left offset */
              text-align: left !important;
            }
            table {
              border-collapse: collapse !important;
              border: 1.5px solid #000000 !important;
            }
          }
          .cell-input {
            width: 100%;
            border: none;
            outline: none;
            background: transparent;
            font-size: 10px;
            font-family: inherit;
            text-align: left !important; /* Left align input content */
            color: #1e293b;
            padding: 4px 6px 4px 8px; /* Consistent left padding */
            font-weight: 600;
          }
          .cell-input:hover {
            background-color: rgba(16, 185, 129, 0.04);
          }
          .cell-input:focus {
            background-color: rgba(16, 185, 129, 0.08);
            box-shadow: inset 0 0 0 1px #34d399;
          }
          .pdf-table {
            border-collapse: collapse;
            width: 100%;
            border: 1.5px solid #000000;
          }
          .pdf-table td {
            border: 1px solid #000000;
            padding: 0;
            font-size: 10px;
            font-family: sans-serif;
            vertical-align: middle;
            color: #000000;
            text-align: left !important; /* Force left alignment */
            padding-left: 8px !important;
          }
          .pdf-label {
            font-weight: bold;
            font-size: 10px;
            color: #000000;
            padding: 4px 6px 4px 8px; /* Consistent left padding */
            text-align: left !important; /* Force left alignment */
          }
        ` }} />

        {/* SUMMARY SHEET TOP TABLE */}
        <table className="pdf-table w-full">
          <tbody>
            
            {/* Table main header */}
            <tr>
              <td colSpan={8} className="pdf-label font-extrabold text-sm uppercase py-2 bg-slate-200 text-left px-3" style={{ backgroundColor: "#d9d9d9", textAlign: "left", paddingLeft: "12px" }}>
                MORGAN PLACEMENT & CONSULTANCY
              </td>
            </tr>
            <tr>
              <td colSpan={8} className="pdf-label font-extrabold text-[11px] py-1.5">
                Summary Sheet
              </td>
            </tr>

            {/* Row 1: Name / Contact */}
            <tr>
              <td colSpan={1} className="pdf-label w-[15%] text-left">Name</td>
              <td colSpan={3} className="w-[35%]">
                <input
                  type="text"
                  className="cell-input text-left font-bold"
                  value={fields.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter Name"
                />
              </td>
              <td colSpan={2} className="pdf-label w-[20%] text-left">Contact No.</td>
              <td colSpan={2} className="w-[30%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.contactNo}
                  onChange={(e) => handleChange("contactNo", e.target.value)}
                  placeholder="Enter Contact"
                />
              </td>
            </tr>

            {/* Row 2: Email / Marital / DOB / Gender */}
            <tr>
              <td colSpan={1} className="pdf-label text-left">Email ID</td>
              <td colSpan={2} className="w-[30%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter Email"
                />
              </td>
              <td colSpan={1} className="pdf-label w-[12%] text-left">Marital Status:</td>
              <td colSpan={1} className="w-[13%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.maritalStatus}
                  onChange={(e) => handleChange("maritalStatus", e.target.value)}
                  placeholder="marital"
                />
              </td>
              <td colSpan={1} className="pdf-label w-[10%] text-left">DOB-</td>
              <td colSpan={1} className="w-[10%]">
                <input
                  type="text"
                  className="cell-input text-left"
                  value={fields.dob}
                  onChange={(e) => handleChange("dob", e.target.value)}
                  placeholder="DOB"
                />
              </td>
              <td colSpan={1} className="w-[10%]">
                <div className="flex items-center text-[11px] font-bold px-1.5">
                  <span className="text-gray-900">Gender:</span>
                  <input
                    type="text"
                    className="w-full border-none outline-none text-center bg-transparent font-bold ml-1"
                    value={fields.gender}
                    onChange={(e) => handleChange("gender", e.target.value)}
                    placeholder="M/F"
                  />
                </div>
              </td>
            </tr>

            {/* Row 3: Role / Reporting */}
            <tr style={{ backgroundColor: "#fff2cc" }}>
              <td colSpan={2} className="pdf-label text-left">Role Applied for</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.roleAppliedFor}
                  onChange={(e) => handleChange("roleAppliedFor", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label text-left">Reporting to (Designation)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.reportingTo}
                  onChange={(e) => handleChange("reportingTo", e.target.value)}
                />
              </td>
            </tr>

            {/* Row 4: Reportees / Revenue Accountability */}
            <tr style={{ backgroundColor: "#fff2cc" }}>
              <td colSpan={2} className="pdf-label text-left">Reportees (Designation)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.reportees}
                  onChange={(e) => handleChange("reportees", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label text-left">Revenue accountability<br />( if applicable)</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.revenueAccountability}
                  onChange={(e) => handleChange("revenueAccountability", e.target.value)}
                />
              </td>
            </tr>

            {/* PG Headers */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td rowSpan={3} colSpan={2} className="pdf-label font-bold text-center border-r border-black">
                <div className="flex flex-col items-center">
                  <span>PG-</span>
                  <input
                    type="text"
                    className="cell-input text-center font-bold text-[10px]"
                    value={fields.pgDegree}
                    onChange={(e) => handleChange("pgDegree", e.target.value)}
                    placeholder="Degree"
                  />
                </div>
              </td>
              <td colSpan={3} className="pdf-label font-bold text-center">Name of Institute</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Year (From -To)</td>
              <td colSpan={1} className="pdf-label font-bold text-center">Score</td>
            </tr>

            {/* PG details inputs */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.pgInstitute}
                  onChange={(e) => handleChange("pgInstitute", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.pgYear}
                  onChange={(e) => handleChange("pgYear", e.target.value)}
                />
              </td>
              <td colSpan={1}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.pgScore}
                  onChange={(e) => handleChange("pgScore", e.target.value)}
                />
              </td>
            </tr>

            {/* PG sub questions */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={3} className="bg-[#e2f0d9]"></td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Full Time /Part Time ( F/P) :</td>
              <td colSpan={1}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.pgTime}
                  onChange={(e) => handleChange("pgTime", e.target.value)}
                />
              </td>
            </tr>

            {/* UG Headers */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td rowSpan={3} colSpan={2} className="pdf-label font-bold text-center border-r border-black">
                <div className="flex flex-col items-center">
                  <span>Graduation-</span>
                  <input
                    type="text"
                    className="cell-input text-center font-bold text-[10px]"
                    value={fields.ugDegree}
                    onChange={(e) => handleChange("ugDegree", e.target.value)}
                    placeholder="Degree"
                  />
                </div>
              </td>
              <td colSpan={3} className="pdf-label font-bold text-center">Name of Institute</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Year (From -To)</td>
              <td colSpan={1} className="pdf-label font-bold text-center">Score</td>
            </tr>

            {/* UG details inputs */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.ugInstitute}
                  onChange={(e) => handleChange("ugInstitute", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.ugYear}
                  onChange={(e) => handleChange("ugYear", e.target.value)}
                />
              </td>
              <td colSpan={1}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.ugScore}
                  onChange={(e) => handleChange("ugScore", e.target.value)}
                />
              </td>
            </tr>

            {/* UG sub questions */}
            <tr style={{ backgroundColor: "#e2f0d9" }}>
              <td colSpan={3} className="bg-[#e2f0d9]"></td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[10px]">Full Time /Part Time ( F/P) :</td>
              <td colSpan={1}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.ugTime}
                  onChange={(e) => handleChange("ugTime", e.target.value)}
                />
              </td>
            </tr>

            {/* 10th and 12th headers & values in 1 row */}
            <tr>
              <td colSpan={2} className="pdf-label font-bold text-center bg-slate-100" style={{ backgroundColor: "#f2f2f2" }}>10th %</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.tenthPercent}
                  onChange={(e) => handleChange("tenthPercent", e.target.value)}
                />
              </td>
              <td colSpan={2} className="pdf-label font-bold text-center bg-slate-100" style={{ backgroundColor: "#f2f2f2" }}>12th %</td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.twelfthPercent}
                  onChange={(e) => handleChange("twelfthPercent", e.target.value)}
                />
              </td>
            </tr>

            {/* Experience / Org headers */}
            <tr style={{ backgroundColor: "#d9e1f2" }}>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Total<br />Experience (in<br />Years)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Organization</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Tenure in Current<br />Organization ( in Years)</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Designation</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.totalExp}
                  onChange={(e) => handleChange("totalExp", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.currentOrg}
                  onChange={(e) => handleChange("currentOrg", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.tenureCurrentOrg}
                  onChange={(e) => handleChange("tenureCurrentOrg", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.currentDesignation}
                  onChange={(e) => handleChange("currentDesignation", e.target.value)}
                />
              </td>
            </tr>

            {/* Location headers */}
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current Location</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Location Applied for</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Native location</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[9px]">Capture other locations s/he might be<br />interested in</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.currentLocation}
                  onChange={(e) => handleChange("currentLocation", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.locationAppliedFor}
                  onChange={(e) => handleChange("locationAppliedFor", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.nativeLocation}
                  onChange={(e) => handleChange("nativeLocation", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.otherLocations}
                  onChange={(e) => handleChange("otherLocations", e.target.value)}
                />
              </td>
            </tr>

            {/* CTC headers */}
            <tr style={{ backgroundColor: "#fce4d6" }}>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Current CTC</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Expected CTC ( Mandatory) or %<br />increase</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Notice Period</td>
              <td colSpan={2} className="pdf-label font-bold text-center text-[10px]">Reason for Job Change</td>
            </tr>
            <tr>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.currentCtc}
                  onChange={(e) => handleChange("currentCtc", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.expectedCtc}
                  onChange={(e) => handleChange("expectedCtc", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.noticePeriod}
                  onChange={(e) => handleChange("noticePeriod", e.target.value)}
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.reasonForChange}
                  onChange={(e) => handleChange("reasonForChange", e.target.value)}
                />
              </td>
            </tr>

            {/* Appraisal headers */}
            <tr style={{ backgroundColor: "#fce4d6" }}>
              <td colSpan={2} className="pdf-label font-bold text-center">Appraisal<br />received Date</td>
              <td colSpan={2} className="pdf-label font-bold text-center">Last Appraisal %</td>
              <td colSpan={2} rowSpan={2} className="text-center font-bold p-1 bg-[#fce4d6] border-r border-black" style={{ verticalAlign: "top" }}>
                <div className="flex flex-col items-center">
                  <span className="text-[10px]">Last Bonus received</span>
                  <input
                    type="text"
                    className="cell-input text-center font-bold mt-1 bg-white border border-gray-300 rounded"
                    value={fields.lastBonus}
                    onChange={(e) => handleChange("lastBonus", e.target.value)}
                  />
                </div>
              </td>
              <td colSpan={2} rowSpan={2} className="text-center font-bold p-1 bg-[#fce4d6]" style={{ verticalAlign: "top" }}>
                <div className="flex flex-col items-center">
                  <span className="text-[10px]">Promotion Due (if any) ( Y/N)</span>
                  <input
                    type="text"
                    className="cell-input text-center font-bold mt-1 bg-white border border-gray-300 rounded"
                    value={fields.promotionDue}
                    onChange={(e) => handleChange("promotionDue", e.target.value)}
                  />
                </div>
              </td>
            </tr>
            <tr style={{ backgroundColor: "#fce4d6" }}>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.appraisalDate}
                  onChange={(e) => handleChange("appraisalDate", e.target.value)}
                  placeholder="(mention due if applicable)"
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.lastAppraisalPercent}
                  onChange={(e) => handleChange("lastAppraisalPercent", e.target.value)}
                  placeholder="(%)"
                />
              </td>
            </tr>

            {/* JD / Target list rows */}
            <tr style={{ backgroundColor: "#fff2cc" }}>
              <td colSpan={3} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether revenue accountability of proposed role has been shared or not :- <br/>(Y/N)
              </td>
              <td colSpan={3} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether is from Target list of Organizations? : <br/>( Y/N)
              </td>
              <td colSpan={2} className="pdf-label text-left font-bold text-[9px] leading-tight">
                Whether the JD has been shared or not :- <br/>(Y/N)
              </td>
            </tr>
            <tr>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.revAccountabilityShared}
                  onChange={(e) => handleChange("revAccountabilityShared", e.target.value)}
                  placeholder="(Y/N)"
                />
              </td>
              <td colSpan={3}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.targetOrg}
                  onChange={(e) => handleChange("targetOrg", e.target.value)}
                  placeholder="(Y/N)"
                />
              </td>
              <td colSpan={2}>
                <input
                  type="text"
                  className="cell-input text-center font-bold"
                  value={fields.jdShared}
                  onChange={(e) => handleChange("jdShared", e.target.value)}
                  placeholder="(Y/N)"
                />
              </td>
            </tr>

            {/* Recruiter Comment Header */}
            <tr>
              <td colSpan={8} className="pdf-label text-left font-extrabold underline py-2 bg-white" style={{ textAlign: "left", paddingLeft: "12px" }}>
                Recruiter Comment:
              </td>
            </tr>
            <tr>
              <td colSpan={8} className="pdf-label text-left font-bold py-1.5 bg-white" style={{ textAlign: "left", paddingLeft: "12px" }}>
                Please mention your assessment of Profile matching with the Job Description in terms of :-
              </td>
            </tr>

            {/* Match of Profile */}
            <tr>
              <td colSpan={8} className="p-2 bg-white border border-black">
                <div className="flex items-center text-[11px] font-bold px-1 py-0.5">
                  <span className="whitespace-nowrap">Match of Profile with the JD ( Yes/No):-</span>
                  <input
                    type="text"
                    className="border-b border-black outline-none font-bold px-1.5 w-24 text-left ml-2 bg-transparent focus:border-emerald-500"
                    value={fields.jdMatch}
                    onChange={(e) => handleChange("jdMatch", e.target.value)}
                  />
                </div>
              </td>
            </tr>

            {/* Communication Skills */}
            <tr>
              <td colSpan={8} className="p-2 bg-white border border-black">
                <div className="flex items-center text-[11px] font-bold px-1 py-0.5">
                  <span className="whitespace-nowrap">Communication Skills ( Spoken English and Articulation ) ( High /Low):-</span>
                  <input
                    type="text"
                    className="border-b border-black outline-none font-bold px-1.5 w-28 text-left ml-2 bg-transparent focus:border-emerald-500"
                    value={fields.communicationSkills}
                    onChange={(e) => handleChange("communicationSkills", e.target.value)}
                  />
                </div>
              </td>
            </tr>

            {/* Technical Skills */}
            <tr>
              <td colSpan={8} className="p-3 bg-white border border-black">
                <div className="flex flex-col gap-1 text-[11px] font-bold px-1">
                  <span className="text-left" style={{ textAlign: "left" }}>
                    Technical Skills ( Whether technical requirements mapped in JD are found in the profile) ( Mention what technical skills found):-
                  </span>
                  <textarea
                    rows={3}
                    className="border border-black outline-none p-2 w-full font-bold bg-transparent rounded-lg focus:ring-1 focus:ring-emerald-500 mt-1 text-left"
                    style={{ textAlign: "left" }}
                    value={fields.technicalSkills}
                    onChange={(e) => handleChange("technicalSkills", e.target.value)}
                    placeholder="Type details here..."
                  />
                </div>
              </td>
            </tr>

            {/* Reason for loading CV */}
            <tr>
              <td colSpan={8} className="p-2 bg-white border border-black">
                <div className="flex items-center text-[11px] font-bold px-1 py-0.5">
                  <span className="whitespace-nowrap">Reason for loading the CV against the job requisition :-</span>
                  <input
                    type="text"
                    className="border-b border-black outline-none font-bold px-1.5 flex-1 ml-2 bg-transparent focus:border-emerald-500"
                    value={fields.reasonForLoading}
                    onChange={(e) => handleChange("reasonForLoading", e.target.value)}
                  />
                </div>
              </td>
            </tr>

          </tbody>
        </table>

      </div>
    </div>
  );
}
